/**
 * PasswordResetModal — HR-only password admin for a single employee.
 *
 * Two flows, one modal:
 *   - 'reset'         — issue a temp password; backend deletes all
 *                       sessions and the temp string is shown ONCE.
 *   - 'force-change'  — flip the must-rotate flag; password unchanged.
 *
 * Both paths confirm before acting. The reset path renders a copy-to-
 * clipboard surface for the returned temp password and reminds HR that
 * the temp will not be shown again. The force-change path simply toasts
 * and closes.
 */

import { useCallback, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  hrForcePasswordChange,
  hrResetPassword,
  type HrResetPasswordResult,
} from '../../api/auth-password'
import { Modal } from '../primitives/Modal'

export type PasswordResetMode = 'reset' | 'force-change'

interface PasswordResetModalProps {
  open: boolean
  onClose: () => void
  mode: PasswordResetMode
  employeeId: string
  employeeName: string
}

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export function PasswordResetModal({
  open,
  onClose,
  mode,
  employeeId,
  employeeName,
}: Readonly<PasswordResetModalProps>): JSX.Element {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetResult, setResetResult] = useState<HrResetPasswordResult | null>(null)
  const [forceDone, setForceDone] = useState(false)
  const [copied, setCopied] = useState(false)

  const reset = useCallback(() => {
    setSubmitting(false)
    setError(null)
    setResetResult(null)
    setForceDone(false)
    setCopied(false)
  }, [])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  const handleConfirm = useCallback(async () => {
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'reset') {
        const result = await hrResetPassword(employeeId)
        setResetResult(result)
      } else {
        await hrForcePasswordChange(employeeId)
        setForceDone(true)
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Action failed.'))
    } finally {
      setSubmitting(false)
    }
  }, [mode, employeeId])

  const handleCopy = useCallback(async () => {
    if (!resetResult) return
    try {
      await navigator.clipboard.writeText(resetResult.tempPassword)
      setCopied(true)
    } catch {
      // Clipboard API can fail in non-secure contexts; the value is still
      // visible in the field so HR can copy manually. Soft-fail.
    }
  }, [resetResult])

  const title =
    mode === 'reset'
      ? `Reset password · ${employeeName}`
      : `Require password change · ${employeeName}`

  return (
    <Modal open={open} onClose={handleClose} title={title} maxWidth={520}>
      <div style={{ padding: '4px 4px 0', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55 }}>
        {resetResult ? (
          <ResetSuccess
            result={resetResult}
            employeeName={employeeName}
            copied={copied}
            onCopy={() => void handleCopy()}
          />
        ) : forceDone ? (
          <ForceChangeSuccess employeeName={employeeName} />
        ) : (
          <Confirmation
            mode={mode}
            employeeName={employeeName}
            submitting={submitting}
            error={error}
            onConfirm={() => void handleConfirm()}
            onCancel={handleClose}
          />
        )}
      </div>

      {(resetResult || forceDone) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            onClick={handleClose}
            style={primaryButton()}
            data-testid="password-reset-done"
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  )
}

// ── Confirmation step ──────────────────────────────────────────────────────

interface ConfirmationProps {
  mode: PasswordResetMode
  employeeName: string
  submitting: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}

function Confirmation({
  mode,
  employeeName,
  submitting,
  error,
  onConfirm,
  onCancel,
}: Readonly<ConfirmationProps>): JSX.Element {
  return (
    <>
      <p style={{ margin: 0 }}>
        {mode === 'reset' ? (
          <>
            Generate a temporary password for <strong>{employeeName}</strong>.
            Their current password becomes invalid and any active sessions
            are signed out. The temp is shown to you <em>once</em> — copy it
            and pass it on out-of-band.
          </>
        ) : (
          <>
            Flag <strong>{employeeName}</strong> to rotate their password at
            next login. Their current password keeps working until they log
            in next; at that point the app blocks them on a change-password
            screen until they pick a new one.
          </>
        )}
      </p>

      {error ? (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 6,
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button
          type="button"
          onClick={onCancel}
          style={ghostButton()}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={primaryButton()}
          disabled={submitting}
          data-testid="password-reset-confirm"
        >
          {submitting ? 'Working…' : mode === 'reset' ? 'Reset password' : 'Require change'}
        </button>
      </div>
    </>
  )
}

// ── Reset result ───────────────────────────────────────────────────────────

interface ResetSuccessProps {
  result: HrResetPasswordResult
  employeeName: string
  copied: boolean
  onCopy: () => void
}

function ResetSuccess({
  result,
  employeeName,
  copied,
  onCopy,
}: Readonly<ResetSuccessProps>): JSX.Element {
  return (
    <>
      <p style={{ margin: 0 }}>
        Temp password issued for <strong>{employeeName}</strong>. They will
        be forced to set a new one at next login. Share the temp out-of-band
        — this screen will not show it again.
      </p>
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          gap: 8,
          alignItems: 'stretch',
        }}
      >
        <input
          readOnly
          value={result.tempPassword}
          aria-label="Temporary password"
          data-testid="password-reset-temp"
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minHeight: 44,
            padding: '8px 12px',
            fontSize: 14,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        />
        <button
          type="button"
          onClick={onCopy}
          style={primaryButton()}
          data-testid="password-reset-copy"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </>
  )
}

// ── Force-change result ────────────────────────────────────────────────────

function ForceChangeSuccess({ employeeName }: Readonly<{ employeeName: string }>): JSX.Element {
  return (
    <p style={{ margin: 0 }}>
      <strong>{employeeName}</strong> will be required to set a new password
      at next login. Their current password keeps working until then.
    </p>
  )
}

// ── Local button styles ────────────────────────────────────────────────────

function primaryButton(): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--steel-blue)',
    color: 'var(--surface)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 36,
  }
}

function ghostButton(): React.CSSProperties {
  return {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-2)',
    fontSize: 13,
    cursor: 'pointer',
    minHeight: 36,
  }
}
